import React, { useState, useEffect } from 'react';
import { formatMoney, unformat } from 'accounting-js';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Link from '../../utils/link';
import Typography from '../../atoms/typography';
import BNPLTab from '../product-detail-accordion';
import FinalSaleWarning from '../final-sale-warning';
import PromoMessaging from '../promo-messaging';

import { useProduct } from '../../utils/context/product-provider';
import getPromoMessages from '../../utils/getPromoMessages';
import theme from '../../theme';
import BazaarVoiceRatings from '../../atoms/bazaar-voice/ratings';

const Brand = styled(Typography)`
  color: ${theme.color.bluePrimary};
  cursor: pointer;
  margin-bottom: 0.2em;
  text-transform: uppercase;
  line-height: ${theme.modularScale.sixteen};
  letter-spacing: 0.1rem;
  margin-bottom: ${theme.modularScale.eight};
`;

const Title = styled(Typography)`
  color: ${theme.color.bluePrimary};
  line-height: ${(props) => props.theme.modularScale.thirtyTwo};
  text-transform: capitalize;
  margin-bottom: ${theme.modularScale.small};

  @media (min-width: ${theme.breakpoint.medium}) {
    font-size: ${(props) => props.theme.modularScale.thirtyTwo};
    line-height: 4rem;
  }
`;

const Price = styled(Typography)`
  color: ${theme.color.bluePrimary};
  justify-content: left;
  letter-spacing: normal;
  line-height: ${theme.modularScale.twentyFour};
  margin-top: 1rem;
  text-transform: lowercase;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  white-space: pre-wrap;
  margin-top: ${theme.modularScale.small};
`;

const FinalSale = styled(Typography)`
  color: ${(props) => props.theme.color.redError};
  font-weight: 400;
  text-transform: capitalize;
  white-space: nowrap;
  margin-left: 0.8rem;
`;

const FinalSaleMessage = styled(FinalSaleWarning)`
  align-items: center;

  @media (min-width: ${theme.breakpoint.medium}) {
    > span {
      line-height: 2.1rem;
      font-size: ${(props) => props.theme.modularScale.fourteen};
    }
  }
`;

const NoUnderlineAnchor = styled.a`
  text-decoration: none;
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  align-content: center;

  ${Brand}, ${Title}, ${Price} {
    text-align: left;
  }
`;

const OptionInvalid = styled.span`
  color: ${(props) => props.theme.color.brandError};
`;

const BNPLWrapper = styled.div``;

const AfterPayMessage = styled.div`
  margin-top: ${theme.modularScale.small};
  font-family: Sans-Serif;
  text-align: left;
  margin-top: 0;
  margin-bottom: 0.5rem;

  afterpay-placement {
    --logo-badge-width: 72px;
    margin-block-start: 0;
    margin-block-end: 0;
  }
`;

const PayPalMessage = styled.div``;

const ProductHeader = (props) => {
  const {
    state: { addedOptionalMonogram }
  } = useProduct();

  const getVendorPrices = () => props?.variant?.prices.filter((v) => v.vendor_id === props.vendor) ?? [];

  const getPrice = () => {
    const priceArray = props?.variant ? getVendorPrices() : props.prices;

    const prices = priceArray.map((p) => ({ original_price: p?.original_price ?? 0.0, price: p?.price ?? 0.0 }));
    const sales = priceArray.filter((p) => p?.on_sale || false);

    const final = { has_sale: false, discount: null };

    const getMin = (arr) =>
      arr.reduce(
        (acc, p) => {
          const a = acc;

          if (+p.price < +a.price && +p.price > 0) {
            a.original_price = +p.original_price;
            a.price = +p.price;
          }

          return a;
        },
        {
          original_price: Math.min.apply(null, arr.map((p) => p.original_price).filter(Boolean)),
          price: Math.min.apply(null, arr.map((p) => p.price).filter(Boolean))
        }
      );

    const getMax = (arr) =>
      arr.reduce(
        (acc, p) => {
          const a = acc;

          if (+p.price > +a.price) {
            a.original_price = +p.original_price;
            a.price = +p.price;
          }

          return a;
        },
        { original_price: +arr[0].original_price, price: +arr[0].price }
      );

    if (sales.length > 0) {
      const percents = sales.map((s) => Math.round(s.discount_percent));

      if (percents.every((p) => p === percents[0])) {
        final.discount = `${Math.round(percents[0])}% Off`;
      } else {
        final.discount = `Up to ${Math.max(...percents)}% Off`;
      }

      final.has_sale = true;
      final.max = getMax(sales);
      final.min = getMin(sales);
    } else {
      final.min = prices.length ? getMin(prices) : { price: 0 };
      final.max = prices.length ? getMax(prices) : { price: 0 };
    }

    if (props.variant && props.variant?.prices[0]?.monogram) {
      const { monogram } = props.variant?.prices[0];

      // if the current variant has
      // optional monogram with
      // an additional cost
      if (monogram.monogrammable && !monogram.monogrammable_only) {
        final.monogram_price = +monogram.monogram_price;
      }
    }

    // This checks for whether any price is NaN and logs message to Sentry
    // const priceIsNaN = (price) => Number.isNaN(Number.parseInt(price, 10));
    // const priceValues = [...(Object.values(final.max)), ...(Object.values(final.min))];

    // TODO: TEC-8327 Handle this appropriately
    // TODO: Remove this Sentry check when the bug is confirmed fixed
    // if (priceValues?.length && priceValues.some(priceIsNaN)) {
    //   Sentry.withScope((scope) => {
    //     scope.setLevel(Sentry.Severity.Warning);
    //     scope.setFingerprint(['{{default}}', 'PriceIsNaN.Undefined']);
    //     Sentry.captureMessage('[TEC-6863] NaN price on PDP');
    //   });
    // }
    return final;
  };

  const [currentPrice, setCurrentPrice] = useState(getPrice());

  const addMonogramPrice = (priceObj, monogramCost) => {
    const final = {};

    final.original_price = priceObj.original_price + monogramCost;
    final.price = priceObj.original_price + monogramCost;

    return final;
  };

  const determineBNPLAmount = () => {
    if (!currentPrice.has_sale && +currentPrice.min.price !== +currentPrice.max.price) {
      return 0;
    }

    return unformat(+currentPrice.min.price);
  };

  useEffect(() => {
    // if user had selected the optional monogram
    // but switched sizes, adjust pricing accordingly if
    // there is a discrepancy between the different size pricings
    if (props.variant && addedOptionalMonogram) {
      const newPrice = getPrice();
      const { max, monogram_price } = newPrice;

      if (max.original_price + monogram_price !== currentPrice.max.original_price) {
        newPrice.max = addMonogramPrice(newPrice.max, newPrice.monogram_price);
        newPrice.min = addMonogramPrice(newPrice.min, newPrice.monogram_price);

        setCurrentPrice(newPrice);
      }
    } else {
      setCurrentPrice(getPrice());
    }
  }, [props.variant]);

  useEffect(() => {
    if (addedOptionalMonogram && currentPrice.monogram_price) {
      const withMonogramPrice = currentPrice;

      withMonogramPrice.max = addMonogramPrice(currentPrice.max, currentPrice.monogram_price);
      withMonogramPrice.min = addMonogramPrice(currentPrice.min, currentPrice.monogram_price);

      setCurrentPrice(withMonogramPrice);
    } else {
      setCurrentPrice(getPrice());
    }
  }, [addedOptionalMonogram]);

  const [activePromoMessage] = useState(getPromoMessages(props.product?.advertised_promotions ?? []));

  useEffect(() => {
    setCurrentPrice(getPrice());
  }, [props.vendor]);

  return (
    <Header>
      {props.brand && (
        <>
          {props.brandSlug ? (
            <Link href={`/brands/${props.brandSlug}`} data-test-id="brand-name-pdp" passHref>
              <NoUnderlineAnchor>
                <Brand element="h2" like="dec-4" data-test-id="brand-name-label-pdp">
                  {props.brand}
                </Brand>
              </NoUnderlineAnchor>
            </Link>
          ) : (
            <Brand element="h2" like="dec-4" data-test-id="brand-name-label-pdp">
              {props.brand}
            </Brand>
          )}
        </>
      )}

      <Link href={`/product/${props.slug}`} passHref>
        <NoUnderlineAnchor>
          <Title element="h1" like="heading-8">
            {props.title}
          </Title>
        </NoUnderlineAnchor>
      </Link>

      {/* TODO: TEC-8758 - For BV we're currently using the slug instead of product
                id because of a backend data issue with the product feed. If the BE
                fix the data issue, we will need to update data-bv-product-id to 
                the correct value. */}
      <BazaarVoiceRatings show="rating_summary" productId={props.slug} />

      <Price tabIndex="0" element="div" like="label-3">
        {props.noOffers || currentPrice.min.price + currentPrice.max.price === 0 ? (
          <OptionInvalid>Out of Stock</OptionInvalid>
        ) : (
          <>
            {currentPrice.has_sale && (
              <>
                {currentPrice.min.price !== currentPrice.max.price && 'From '}
                <Price
                  element="span"
                  like="label-3"
                  data-test-id="product-original-price-pdp"
                  css={() => ({
                    textDecoration: 'line-through',
                    color: theme.color.brandLightBlue,
                    marginTop: 0,
                    marginRight: '0.4rem'
                  })}>
                  {formatMoney(currentPrice.min.original_price)}
                </Price>
                <Price
                  element="span"
                  like="label-3"
                  data-test-id="discount-price-pdp"
                  css={() => ({
                    marginTop: 0,
                    marginRight: '0.4rem'
                  })}>
                  {formatMoney(currentPrice.min.price)}
                </Price>
                <Price
                  element="span"
                  like="label-3"
                  data-test-id="additional-discount-pdp"
                  css={() => ({
                    color: theme.color.redError,
                    marginTop: 0
                  })}>
                  {`(${currentPrice.discount})`}
                </Price>
              </>
            )}

            {!currentPrice.has_sale && (
              <>
                {+currentPrice.min.price === +currentPrice.max.price
                  ? formatMoney(currentPrice.min.price)
                  : `${formatMoney(currentPrice.min.price)} - ${formatMoney(currentPrice.max.price)}`}
              </>
            )}
          </>
        )}

        {(props.finalSale || props.isGiftCard) && (
          <FinalSale element="span" like="label-3">
            Final sale!
          </FinalSale>
        )}
      </Price>

      {(props.finalSale || props.isGiftCard) && <FinalSaleMessage />}

      {activePromoMessage && activePromoMessage.length > 0
        ? activePromoMessage.map((activePromoObject) => (
            <PromoMessaging key={activePromoObject.id} pdpText={activePromoObject?.advertised_text} />
          ))
        : null}

      {!props.isMobilePaid && (
        <BNPLTab
          title="or 4 interest-free payments"
          product={props.product}
          dataTestId="pay-options-dropdown-pdp"
          isBNPL>
          <BNPLWrapper>
            <AfterPayMessage>
              <PayPalMessage
                data-pp-message
                data-pp-style-layout="text"
                data-pp-style-logo-type="inline"
                data-pp-style-text-color="black"
                data-pp-style-text-align="left"
                data-pp-amount={determineBNPLAmount()}
              />
              <afterpay-placement
                data-locale="en_US"
                data-currency="USD"
                data-intro-text="Pay in"
                data-modal-link-style="learn-more-text"
                data-badge-theme="white-on-black"
                data-size={'xs'}
                data-modal-theme="white"
                data-amount={determineBNPLAmount()}
              />
            </AfterPayMessage>
          </BNPLWrapper>
        </BNPLTab>
      )}
    </Header>
  );
};

ProductHeader.defaultProps = {
  title: '',
  brand: '',
  brandSlug: '',
  slug: '',
  variant: false,
  vendor: 0,
  noOffers: false,
  finalSale: false,
  isGiftCard: false,
  isMobilePaid: false
};

ProductHeader.propTypes = {
  product: PropTypes.object.isRequired,
  brand: PropTypes.string,
  brandSlug: PropTypes.string,
  slug: PropTypes.string,
  title: PropTypes.string,
  prices: PropTypes.array.isRequired,
  variant: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  vendor: PropTypes.number,
  noOffers: PropTypes.bool,
  finalSale: PropTypes.bool,
  isGiftCard: PropTypes.bool,
  isMobilePaid: PropTypes.bool
};

ProductHeader.whyDidYouRender = true;

export default ProductHeader;
